import { recordListUserInfo, SESSION_KEYS } from '../config/CustomEnums';
import {
  TRANSACTION_EVENT_TYPE,
  TRANSACTION_RECORD_TYPE,
} from '../containers/record/records/TransactionList';
import {
  approveTransaction,
  deleteTransactions,
  getPurchasedItemAdditionalDataFields,
  getSimpleTransactions,
  getTransaction,
  getTransactionAdditionalDataFields,
  getTransactions,
  recallTransaction,
  rejectTransaction,
} from '../services/TransactionRecordsAPIHelper';
import {
  convertCursorToNumber,
  convertNumberToCursor,
  createAction,
  formatNumberWithCommas,
  getDisplayLastModifiedBy,
} from '../utils';
import {
  TimeFormater,
  formatDate,
  getCouponExpiryDate,
  getDetailPageDisplayLastModifiedDate,
  getDisplayDate,
  getListDisplayLastModifiedDate,
} from '../utils/TimeFormatUtil';
import { loading, apiWithResponseHandle } from './LoadingUtil';
import { createModel } from './BaseModel';

const getInitialState = () => ({
  transactionList: [],
  listDisplayFields: [
    { displayName: 'ID', fieldName: 'pk', linked: true },
    ...recordListUserInfo,
    {
      displayName: 'Creation time',
      fieldName: 'listDisplayCreationDate',
      orderField: 'creationDate',
    },
    {
      displayName: 'Transaction Date',
      fieldName: 'displayTransactionDate',
      orderField: 'date',
    },
    { displayName: 'Store', fieldName: 'displayStoreName', orderField: 'store' },
    { displayName: 'Gift card code', fieldName: 'giftCardCode' },
    {
      displayName: 'Eligible Total',
      fieldName: 'totalValue',
      orderField: 'totalValue',
    },
    { displayName: 'Paid amount', fieldName: 'paidAmount' },
    { displayName: 'Transaction Type', fieldName: 'transactionDisplayType' },
    { displayName: 'Online Event Type', fieldName: 'onlineEventType' },
    { displayName: 'Offline Event Type', fieldName: 'displayOfflineEventType' },
    { displayName: 'Last modified time', fieldName: 'listDisplayLastModifiedTime' },
  ],
  pageInfo: {
    startCursor: '',
    endCursor: '',
    hasNextPage: false,
    hasPreviousPage: false,
  },
  currentLastCursor: '',
  currentPage: 0,
  totalPage: 0,
  totalCount: 0,
  checkedList: [],
  currentPageTransactionList: [],
  selectedTransaction: {},
  transactionAdditionalDataFields: [],
  transactionAdditionalDataList: [],
  purchasedItemAdditionalDataFields: [],
  purchasedItemAdditionalDataList: [],
});

export const parseTransactionDisplayType = (type) => {
  let displayType = '';
  switch (type) {
    case 'ONLINE':
      displayType = TRANSACTION_RECORD_TYPE.TYPE_ONLINE;
      break;
    case 'OFFLINE_POS':
      displayType = TRANSACTION_RECORD_TYPE.TYPE_OFFLINE_POS;
      break;
    case 'OFFLINE_REWARD_CLAIM':
      displayType = TRANSACTION_RECORD_TYPE.TYPE_OFFLINE_REWARD_CLAIM;
      break;
    default:
      break;
  }
  return displayType;
};

const parseEventType = (eventType) => {
  let displayEventType = '';
  switch (eventType) {
    case 'PENDING':
      displayEventType = TRANSACTION_EVENT_TYPE.TYPE_PENDING;
      break;
    case 'AUTHORIZED':
      displayEventType = TRANSACTION_EVENT_TYPE.TYPE_AUTHORIZED;
      break;
    case 'PAID':
      displayEventType = TRANSACTION_EVENT_TYPE.TYPE_PAID;
      break;
    case 'PARTIALLY_PAID':
      displayEventType = TRANSACTION_EVENT_TYPE.TYPE_PARTIALLY_PAID;
      break;
    case 'REFUNDED':
      displayEventType = TRANSACTION_EVENT_TYPE.TYPE_REFUNDED;
      break;
    case 'PARTIALLY_REFUNDED':
      displayEventType = TRANSACTION_EVENT_TYPE.TYPE_PARTIALLY_REFUNDED;
      break;
    case 'VOIDED':
      displayEventType = TRANSACTION_EVENT_TYPE.TYPE_VOIDED;
      break;
    case 'WAITING_FOR_APPROVAL':
      displayEventType = TRANSACTION_EVENT_TYPE.TYPE_WAITING;
      break;
    case 'APPROVED':
      displayEventType = TRANSACTION_EVENT_TYPE.TYPE_APPROVED;
      break;
    case 'RECALLED':
      displayEventType = TRANSACTION_EVENT_TYPE.TYPE_RECALLED;
      break;
    case 'REJECTED':
      displayEventType = TRANSACTION_EVENT_TYPE.TYPE_REJECTED;
      break;
    case 'EXCHANGE':
      displayEventType = TRANSACTION_EVENT_TYPE.TYPE_EXCHANGE;
      break;
    default:
      break;
  }
  return displayEventType;
};

const parsePurchaseItems = (purchasedItems) => {
  const parsedPurchaseList = purchasedItems.map((item) => {
    const data = item.node;
    const extraFields = (data.extraFields && JSON.parse(data.extraFields)) || {}
    const additionalData = Object.keys(extraFields)?.map((key) => {
      return {
        title: key,
        value: typeof (extraFields[key]) === "string"
          ? extraFields[key]
          : extraFields[key] === null
            ? "" : JSON.stringify(extraFields[key])
      }
    })
    const shopifyCollections = (JSON.parse(data.shopifyCollections || '') || []).map(collection=>collection.title)
    const shopifyTags = data.shopifyTags || []

    return {
      productName: data.name,
      sku: data.sku,
      category: data.category,
      subcategory: data.subcategory,
      shopifyCollections: shopifyCollections || [],
      shopifyTags: shopifyTags,
      displayShopifyCollections: shopifyCollections?.join(","),
      displayShopifyTags: shopifyTags?.join(","),
      brand: data.brand,
      quantity: data.quantity,
      originalPrice: data.originalPrice,
      displayOriginalPrice: data.originalPrice
        ? `HK$ ${formatNumberWithCommas(data.originalPrice)}`
        : '',
      value: data.value,
      displayValue: data.value
        ? `HK$ ${formatNumberWithCommas(data.value)}`
        : '',
      netSales: data.netSales,
      displayNetSales: data.netSales
        ? `HK$ ${formatNumberWithCommas(data.netSales)}`
        : '',
      refundedQuantity: data.refundedQuantity || null,
      id: data.id,
      pk: data.pk,
      extraFields: extraFields,
      additionalData: additionalData,
      discounts: data?.discounts?.edges?.map((item) => {
        return {
          pk: item.node?.pk,
          code: item.node?.code,
          valueType: item.node.valueType,
          type: item.node.type,
          value: item.node.value,
        }
      }),
      status: data.status,
      isRefunded: ["REFUNDED", "PARTIALLY_REFUNDED"].includes(data.status),
    };
  });
  return parsedPurchaseList;
};
const parseTransactionRewards = (transaction) => {
  const pointReward = transaction.rewardPointTransaction?.map(item=> {
    const isCashDollarUsed = [
      "CUSTOMER_USED_CASH_DOLLAR",
      "CUSTOMER_USED_CASH_DOLLAR_BY_MERCHANT_SCANNING",
      "MERCHANT_USE_CASH_DOLLAR",
      "AUTO_ADMIN_EDITED_CASH_DOLLAR",
    ].includes(item?.transactionType)
    
    return {
      pk: item?.pk,
      rewardType: "Points",
      pointType: item?.pointType,
      displayPoints: `${item?.pointType === "TYPE_POINT_REMOVE" ? "-" : "+"} ${item?.value || 0} points`,
      displayTpe: `${item?.pointType === "TYPE_POINT_REMOVE" ? "-" : "+"} ${item?.tpe || 0} progress points`,
      creationDate: item.pointRecordCreatedTime,
      displayCreationDate: formatDate(item.pointRecordCreatedTime, "DD MMM yyyy, HH:mm:ss"),
      isCashDollarUsed,
    }
  }) ||[]
  const couponReward = transaction.rewardCouponTransaction?.map(item => ({
    pk: item?.pk,
    rewardType: "Coupons",
    type: item?.type,
    displayCoupon: `${["EARN", "GRANT"].includes(item?.type) ? "+" : "-"} 1 coupons`,
    creationDate: item.creationDate,
    displayCreationDate: formatDate(item.creationDate, "DD MMM yyyy, HH:mm:ss"),
    isCouponUsed: item?.isCouponUsed,
  })) || []
  const stampReward = transaction.rewardStampRecord?.map(item => ({
    pk: item?.pk,
    rewardType: "Stamps",
    eventType: item?.eventType,
    displayStamps: `${item?.eventType === "REMOVE_STAMP" ? "-" : "+"} ${item.stampQuantity} stamps`,
    creationDate: item.creationDate,
    displayCreationDate: formatDate(item.creationDate, "DD MMM yyyy, HH:mm:ss"),
  })) || []
  return {
    pointReward,
    couponReward,
    stampReward,
  }
}

const parseTransaction = (data) => {
  const purchasedItems = data.purchasedItems?.edges || [];
  const virtualGiftCards = data.virtualGiftCards?.edges || [];
  const giftCardCode = virtualGiftCards.map((item) => item.node.code);
  const customer = data.customer || {};
  const extraFields = (data.extraFields && JSON.parse(data.extraFields)) || {}
  const additionalData = Object.keys(extraFields)?.map((key)=>{
    return {
      title: key,
      value: typeof (extraFields[key]) === "string"
        ? extraFields[key]
        : extraFields[key] === null
          ? "" : JSON.stringify(extraFields[key])
    }
  })
  const extraStoreName = data.store?.visibleInFrontEnd ? '' : '(closed)';

  const transactionPurchasedItems = purchasedItems.length > 0
    ? parsePurchaseItems(purchasedItems)
    : [];

  let transactionTotalQuantity = data.totalQuantity || 0;
  if (!transactionTotalQuantity) {
    transactionPurchasedItems.map(el => {
      if (el.isRefunded) {
        transactionTotalQuantity += (el.quantity || 0) - (el.refundedQuantity || 0);
      } else {
        transactionTotalQuantity += el.quantity || 0;
      };
    });
  }

  let transactionOriginalProductAmount = 0;
  transactionPurchasedItems.map(el => {
    let quantity = el.quantity || 0;
    if (el.isRefunded) {
      quantity -= el.refundedQuantity || 0;
    };
    transactionOriginalProductAmount += (el.originalPrice || el.value) * quantity || 0;
  });
  transactionOriginalProductAmount = Number(transactionOriginalProductAmount).toFixed(2);

  return {
    pk: data.pk,
    id: data.id,
    customerIsReset: customer.isReset,
    customerId: customer.pk,
    membershipId: customer.membershipId,
    mobilePhoneNumber: customer.mobilePhoneNumberSubscriberNumber
    ? `+${customer.mobilePhoneNumberCountryCode}\r\n${customer.mobilePhoneNumberSubscriberNumber}` : null,
    email: customer.emailAddress,
    ssoUid: customer.ssoUid,
    name: data.customer
      ? customer.nickname
        ? `${customer.firstName} ${customer.lastName} (${customer.nickname})`
        : `${customer.firstName} ${customer.lastName}`
      : null,
    transactionType: data.transactionType,
    transactionDisplayType: parseTransactionDisplayType(data.transactionType),
    storeName: data.store?.name,
    creationDate: data.creationDate,
    listDisplayCreationDate: getListDisplayLastModifiedDate(data.creationDate),
    listDisplayLastModifiedTime: getListDisplayLastModifiedDate(data?.lastModifiedDate),
    detailDisplayCreationDate: getDetailPageDisplayLastModifiedDate(data?.creationDate),
    detailDisplayLastModifiedTime: getDetailPageDisplayLastModifiedDate(data?.lastModifiedDate),
    displayLastModifiedBy: getDisplayLastModifiedBy(data),
    transactionDate: data.date,
    displayTransactionDate: getDisplayDate(data.date),
    // transactionDetailDisplayDate: formatDate(
    //   data.date,
    //   data.transactionType === 'OFFLINE_REWARD_CLAIM'
    //     ? TimeFormater.dayMonthYearWeek
    //     : TimeFormater.dayMonthYearWeekTimeA,
    // ),
    transactionDetailDisplayDate: getDetailPageDisplayLastModifiedDate(
      data.date
    ),
    purchasedDate: data.purchasedDate,
    transactionDetailDisplayPurchasedTime: getDetailPageDisplayLastModifiedDate(
      data.purchasedDate,
    ),
    uploadDate: data.uploadDate,
    showUploadDate: !!data.uploadDate,
    transactionDetailDisplayUploadTime: getDetailPageDisplayLastModifiedDate(
      data.uploadDate,
    ),
    transactionDetailCreationDate: formatDate(
      data.creationDate,
      TimeFormater.dayMonthYearWeekTimeA,
    ),
    giftCardCode: giftCardCode.join(', '),
    pointToCash: data.pointToCash,
    displayPointToCash: data.pointToCash
      ? `HK$ ${formatNumberWithCommas(data.pointToCash)}`
      : '',
    totalValue: data.totalValue,
    displayTotalValue: data.totalValue
      ? `HK$ ${formatNumberWithCommas(data.totalValue)}`
      : '',
    paidAmount: data.paidAmount,
    displayPaidAmount: data.paidAmount
      ? `HK$ ${formatNumberWithCommas(data.paidAmount)}`
      : '',
    netSales: data.netSales,
    displayNetSales: data.netSales
      ? `HK$ ${formatNumberWithCommas(data.netSales)}`
      : '',
    totalQuantity: data.totalQuantity,
    displayTotalQuantity: transactionTotalQuantity,
    originalProductAmount: transactionOriginalProductAmount,
    displayOriginalProductAmount: transactionOriginalProductAmount
      ? `HK$ ${formatNumberWithCommas(transactionOriginalProductAmount)}`
      : '',
    onlineEventType: parseEventType(data.onlineEventType),
    offlineEventType: data.offlineEventType,
    displayOfflineEventType: parseEventType(data.offlineEventType),
    staffName: data.staffName,
    shippingFee: data.shippingFee,
    displayShippingFee: data.shippingFee
      ? `HK$ ${formatNumberWithCommas(data.shippingFee)}`
      : '',
    otherCharge: data.otherCharge,
    displayOtherCharge: data.otherCharge
      ? `HK$ ${formatNumberWithCommas(data.otherCharge)}`
      : '',
    remarks: data.remarks,
    posInvoiceId: data.posInvoiceId,
    invoiceId: data.invoiceId,
    receiptImage: data.receiptImage,
    creditCardSlipImage: data.creditCardSlipImage,
    purchasedItems: transactionPurchasedItems,
    deleteDisplayName: `${getCouponExpiryDate(data.creationDate)} ${data.customer?.firstName
      } ${data.customer?.lastName} $${data.totalValue}`,
    customer: {
      pk: data.customer?.pk,
      ssoUid: data.customer?.ssoUid,
      firstName: data.customer?.firstName,
      lastName: data.customer?.lastName,
      nickname: data.customer?.nickname,
      owner: data.customer ? `${data.customer?.firstName} ${data.customer?.lastName}` : null,
      membershipId: data.customer?.membershipId,
    },
    store: {
      storeID: data.store?.id,
      storePK: data.store?.pk,
      storeName: data.store?.name,
      displayStoreName: data.store?.name ? `${data.store?.name}${extraStoreName}` : null,
    },
    createdDate: formatDate(data.creationDate),
    eventType:
      parseEventType(data.onlineEventType) ||
      parseEventType(data.offlineEventType) ||
      '-',
    displayStoreName: data.store?.name ? `${data.store?.name}${extraStoreName}` : null,
    transactionValue: data.totalValue,
    transactionPaidAmount: data.paidAmount,
    brand: data.store?.brand?.name,
    extraFields: extraFields,
    additionalData: additionalData,
    discountAmount: data?.discountAmount,
    displayDiscountAmount: data.discountAmount
      ? `HK$ ${formatNumberWithCommas(data.discountAmount)}`
      : '',
    discounts: data?.discounts?.edges?.map((item) => {
      return {
        pk: item.node?.pk,
        code: item.node?.code,
        valueType: item.node.valueType,
        type: item.node.type,
        value: item.node.value,
      }
    }),
    refundType: data.refundType,
    refundAmount: data.refund,
    refundedInvoiceId: data.refundedInvoiceId,
    refundedTransactionId: data.refundedTransactionId,
    relatedRefundedTransactions: data.relatedRefundedTransactions,
    ...parseTransactionRewards(data),
  };
};

export default createModel({
  namespace: 'transactions',
  states: getInitialState(),
  params: {
    sessionKey: SESSION_KEYS.TRANSACTION_SESSION_KEY,
    listAPI: getTransactions,
    parse: (data) => data?.transactions?.edges?.map((item) => parseTransaction(item.node)),
    objectKey: 'transactionList',
  },

  reducers: {
    updateState(state, { payload }) {
      return { ...state, ...payload };
    },

    updateTransactionList(state, { payload }) {
      const { page, currentPageTransactionList } = payload;
      return {
        ...state,
        transactionList: page > 1
          ? [...state.transactionList, ...currentPageTransactionList]
          : currentPageTransactionList,
       };
    },
    clearSelectedTransaction(state, { payload }) {
      return {
        ...state,
        selectedTransaction: {},
      };
    },

    clearData(state, { payload }) {
      return {
        ...state,
        ...getInitialState(),
      };
    },
    updateAllTransactionAdditionalDataFields(state, { payload }) {
      const { page, additionalDataList, additionalDataFields } = payload;
      return {
        ...state,
        transactionAdditionalDataList:
          page > 1
            ? [...state.transactionAdditionalDataList, ...additionalDataList]
            : additionalDataList,
        transactionAdditionalDataFields:
          page > 1
            ? [...state.transactionAdditionalDataFields, ...additionalDataFields]
            : additionalDataFields,
      };
    },
    updateAllPurchasedItemAdditionalDataFields(state, { payload }) {
      const { page, additionalDataList, additionalDataFields } = payload;
      return {
        ...state,
        purchasedItemAdditionalDataList:
          page > 1
          ? [...state.purchasedItemAdditionalDataList, ...additionalDataList]
          : additionalDataList,
        purchasedItemAdditionalDataFields:
          page > 1
            ? [...state.purchasedItemAdditionalDataFields, ...additionalDataFields]
            : additionalDataFields,
      };
    }
  },

  effects: {
    getSimpleTransactionsForSelector: [
      function* ({ payload }, { call, all, put }) {
        const transactionIds = payload?.transactionIds
        const page = payload.page !== undefined ? payload.page : 1;
        const pageCursor = payload.page
          ? convertNumberToCursor((page - 1) * 20 - 1)
          : '';
        const serviceArgs = [
          getSimpleTransactions,
          pageCursor,
          payload.search || '',
          transactionIds,
        ];
        function* onSuccess(data) {
          const transactionData = data.transactions.edges;
          const transactionList = transactionData.map((item) => {
            return parseTransaction(item.node);
          });
          yield put(
            createAction('updateTransactionList')({
              page,
              currentPageTransactionList: transactionList,
            }),
          );
        }
        // eslint-disable-next-line require-yield
        function* onFailed(data) {
          console.log('@@372: ', data);
        }
        yield apiWithResponseHandle(serviceArgs, onSuccess, onFailed);
      },
      { type: 'takeLatest' },
    ],
    getCurrentPageTransactions: [
      function* ({ payload }, { call, put, select }) {
        const { page } = payload;
        let afterCursor = undefined;
        if (page > 1) {
          afterCursor = btoa(`arrayconnection:${(page - 1) * 20 - 1}`);
        }
        const serviceArgs = [getTransactions, afterCursor, payload];
        function* onSuccess(data) {
          const transactionData = data.transactions.edges;
          const pageInfo = data.transactions.pageInfo;
          const totalCount = data.transactions.totalCount;
          const currentLastCursor = pageInfo.endCursor;
          const transactionList = transactionData.map((item) => {
            return parseTransaction(item.node);
          });
          yield put(
            createAction('updateState')({
              currentPageTransactionList: transactionList,
              pageInfo: {
                startCursor: convertCursorToNumber(pageInfo?.startCursor) + 1,
                endCursor: convertCursorToNumber(pageInfo?.endCursor) + 1,
              },
              currentLastCursor,
              totalCount,
              totalPage: Math.ceil(totalCount / 20),
            }),
          );

          if (payload.isSelectorLoad) {
            // const allList = select((state) => state.allTransactionList) || []
            const { allList } = yield select((state) => ({
              allList: state.transactions.allTransactionList || [],
            }));
            yield put(
              createAction('updateState')({
                allTransactionList: page === 1 ? transactionList : [...allList, ...transactionList]
              })
            )
          }
        }
        yield loading(serviceArgs, onSuccess);
      },
      {
        type: 'takeLatest',
      },
    ],
    getPagedTransactionList: [
      function* ({ payload }, { put, select }) {
        const page = payload.page || 1;
        const pageCursor = payload.page
          ? convertNumberToCursor((page - 1) * 20 - 1)
          : '';
        const serviceArgs = [getTransactions, pageCursor, payload];
        function* onSuccess(data) {
          const pageInfo = data?.transactions?.pageInfo;
          const currentLastCursor = pageInfo?.endCursor;
          const totalCount = data?.transactions?.totalCount;

          const transactionList = data?.transactions?.edges.map((item) =>
          parseTransaction(item.node),
          );
          if (payload.isSelectorLoad) {
            const stateTransactionList = yield select(
              (state) => state.transactions.transactionList,
            );
            yield put({
              type: 'updateState',
              payload: {
                transactionList:
                  page > 1 ? [...stateTransactionList, ...transactionList] : transactionList,
              },
            });
          } else {
            yield put({
              type: 'updateState',
              payload: {
                pagedTransactionList: transactionList,
                pageInfo: {
                  startCursor: convertCursorToNumber(pageInfo.startCursor) + 1,
                  endCursor: convertCursorToNumber(pageInfo.endCursor) + 1,
                },
                currentLastCursor,
                totalCount,
                totalPage: Math.ceil(totalCount / 20),
              },
            });
          }
        }
        if (payload?.isSelectorLoad) {
          yield apiWithResponseHandle(serviceArgs, onSuccess);
        } else {
          yield loading(serviceArgs, onSuccess);
        }
      },
      { type: 'takeLatest' },
    ],
    getTransaction: [
      function* ({ payload }, { call, put }) {
        const { transactionPK } = payload;
        const afterActions = payload.afterActions || (() => { });
        const transactionID = btoa(`TransactionNode:${transactionPK}`);
        const serviceArgs = [getTransaction, transactionID];
        function* onSuccess(data) {
          const transactionData = data.transaction;
          const parsedTransaction = parseTransaction(transactionData)
          yield put(
            createAction('updateState')({
              selectedTransaction: parsedTransaction,
            }),
          );
          yield put(
            createAction('createTransaction/updateState')({
              transaction: parsedTransaction,
              hasUpdatedDefaultValues: true,
            }),
          );
          afterActions();
        }
        yield loading(serviceArgs, onSuccess);
      },
      { type: 'takeLatest' },
    ],
    approveTransaction: [
      function* ({ payload }, { put }) {
        const { transactionPK } = payload;
        const serviceArgs = [approveTransaction, transactionPK];
        function* onSuccess() {
          const afterActions = payload.afterActions || (() => { });
          yield afterActions();
        }
        yield loading(serviceArgs, onSuccess);
      },
      {
        type: 'takeLatest',
      },
    ],
    rejectTransaction: [
      function* ({ payload }, { put }) {
        const { transactionPK } = payload;
        const serviceArgs = [rejectTransaction, transactionPK];
        function* onSuccess() {
          const afterActions = payload.afterActions || (() => { });
          yield afterActions();
        }
        yield loading(serviceArgs, onSuccess);
      },
      {
        type: 'takeLatest',
      },
    ],
    recallTransaction: [
      function* ({ payload }, { put }) {
        const { transactionPK } = payload;
        const serviceArgs = [recallTransaction, transactionPK];
        function* onSuccess() {
          const afterActions = payload.afterActions || (() => { });
          yield afterActions();
        }
        yield loading(serviceArgs, onSuccess);
      },
      { type: 'takeLatest' },
    ],
    deleteTransaction: [
      function* ({ payload }, { put }) {
        const { ids } = payload;
        const serviceArgs = [deleteTransactions, ids];
        function* onSuccess() {
          const afterActions = payload.afterActions || (() => { });
          yield afterActions();
        }
        yield loading(serviceArgs, onSuccess);
      },
      { type: 'takeLatest' },
    ],
    getPagedTransactionAdditionDataFields: [
      function* ({ payload }, { select, put }) {
        const { actionAfterSuccess, search, page } = payload
        const afterCursor = page
          ? convertNumberToCursor((page - 1) * 20 - 1)
          : '';
        const serviceArgs = [getTransactionAdditionalDataFields, afterCursor, search];
        function* onSuccess(data) {
          const additionalDataList = data?.transactionAdditionalDataFields?.edges?.map(item => ({
            ...item.node,
            name: item.node?.fieldName,
          }))
          const additionalDataFieldKeys = data?.transactionAdditionalDataFields?.edges?.map(item => item?.node?.fieldName)
          yield put({
            type: 'updateAllTransactionAdditionalDataFields',
            payload: {
              additionalDataList,
              additionalDataFields: additionalDataFieldKeys,
              page: page,
            },
          });
          yield actionAfterSuccess && actionAfterSuccess(additionalDataFieldKeys)
        }
        yield apiWithResponseHandle(serviceArgs, onSuccess);
      },
      { type: 'takeLatest' },
    ],
    getPagedPurchasedItemAdditionDataFields: [
      function* ({ payload }, { select, put }) {
        const { actionAfterSuccess, search, page } = payload
        const afterCursor = page
          ? convertNumberToCursor((page - 1) * 20 - 1)
          : '';
        const serviceArgs = [getPurchasedItemAdditionalDataFields, afterCursor, search];
        function* onSuccess(data) {
          const additionalDataList = data?.purchasedItemAdditionalDataFields?.edges?.map(item => ({
            ...item.node,
            name: item.node?.fieldName,
          }))
          const additionalDataFieldKeys = data?.purchasedItemAdditionalDataFields?.edges?.map(item => item?.node?.fieldName)
          yield put({
            type: 'updateAllPurchasedItemAdditionalDataFields',
            payload: {
              additionalDataList,
              additionalDataFields: additionalDataFieldKeys,
              page: page,
            },
          });
          yield actionAfterSuccess && actionAfterSuccess(additionalDataFieldKeys)
        }
        yield apiWithResponseHandle(serviceArgs, onSuccess);
      },
      { type: 'takeLatest' },
    ]
  },
});
